/**
 * 표 머리의 정렬 단추와 거르개 — 규칙은 `tableView.ts`, 여기는 그리는 쪽.
 *
 * 모든 열에 달지 않는다. 자주 찾는 기준(이름, 마지막 신호, 남은 공간, 버전 …)에만 단다 —
 * 열마다 화살표와 깔때기가 붙으면 정작 쓸 것이 어느 것인지 눈에 띄지 않는다.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter } from 'lucide-react';
import { placeBubble } from './HelpTip.js';
import { nextSort, readSort, writeSort, type FilterOption, type SortDir, type SortState } from './tableView.js';

/** 정렬 상태 — 표마다 브라우저에 기억한다. */
export function useTableSort<K extends string>(
  table: string,
  keys: readonly K[],
  fallback: SortState<K>,
): [SortState<K>, (key: K, firstDir: SortDir) => void, (next: SortState<K>) => void] {
  const [sort, setSort] = useState<SortState<K>>(() => readSort(table, keys, fallback));
  const toggle = useCallback(
    (key: K, firstDir: SortDir) => {
      setSort((current) => {
        const next = nextSort(current, key, firstDir);
        writeSort(table, next);
        return next;
      });
    },
    [table],
  );
  /** 머리가 없는 보기(콘텐츠 그리드)의 정렬 고르기가 쓴다. */
  const choose = useCallback(
    (next: SortState<K>) => {
      writeSort(table, next);
      setSort(next);
    },
    [table],
  );
  return [sort, toggle, choose];
}

interface SortProps<K extends string> {
  sort: SortState<K>;
  onSort: (key: K, firstDir: SortDir) => void;
  /** 처음 눌렀을 때의 방향 — 그 열에서 먼저 보고 싶은 것이 위로 오게. */
  firstDir?: SortDir;
  /** 방향마다 읽는 말("적은 순" · "많은 순") — 화살표만으로는 상대 시각 열의 위아래가 헷갈린다. */
  dirLabels: Record<SortDir, string>;
}

export function ColumnHead<K extends string>({
  label,
  column,
  sorting,
  filter,
  className,
}: {
  label: string;
  column: K;
  sorting?: SortProps<K>;
  filter?: ReactNode;
  className?: string;
}): ReactNode {
  const active = sorting?.sort.key === column;
  const dir = active ? sorting.sort.dir : null;
  const Icon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <th className={className} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}>
      <span className="th-tools">
        {sorting ? (
          <button
            type="button"
            className={`th-sort${active ? ' is-active' : ''}`}
            title={dir ? `${sorting.dirLabels[dir]} — 누르면 ${sorting.dirLabels[dir === 'asc' ? 'desc' : 'asc']}` : `${sorting.dirLabels[sorting.firstDir ?? 'asc']}으로 정렬`}
            onClick={() => sorting.onSort(column, sorting.firstDir ?? 'asc')}
          >
            {label}
            <Icon aria-hidden="true" className="th-sort-icon" strokeWidth={2.25} />
          </button>
        ) : (
          label
        )}
        {filter}
      </span>
    </th>
  );
}

/**
 * 열 거르개 — 깔때기를 누르면 그 열에 있는 값과 수가 뜨고, 보일 것을 고른다.
 *
 * 고르는 즉시 표가 바뀐다(확인 단추 없음). 목록은 표 밖(`document.body`)에 띄운다 —
 * 표를 감싼 상자가 옆으로 밀리는(`overflow-x`) 자리라, 안에 두면 잘린다.
 */
export function FilterMenu({
  label,
  options,
  chosen,
  onChange,
}: {
  label: string;
  options: readonly FilterOption[];
  chosen: readonly string[];
  onChange: (next: string[]) => void;
}): ReactNode {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const total = options.reduce((sum, option) => sum + option.count, 0);
  const active = chosen.length > 0;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (refocus) anchor.current?.focus();
  }, []);

  /*
   * 그릴 때마다 자리를 다시 잰다. 값을 고르면 표의 줄이 바뀌어 열 너비가 달라지고, 깔때기가 옆으로
   * 옮겨 간다 — 연 순간의 자리에 두면 목록이 엉뚱한 열 위에 떠 있게 된다.
   */
  useLayoutEffect(() => {
    if (!open || !anchor.current || !menu.current) return;
    const a = anchor.current.getBoundingClientRect();
    const b = menu.current.getBoundingClientRect();
    const next = placeBubble(
      { top: a.top, bottom: a.bottom, left: a.left, width: a.width },
      { width: b.width, height: b.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition((current) =>
      current && current.top === next.top && current.left === next.left ? current : { top: next.top, left: next.left },
    );
  });

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLInputElement>('input')?.focus();
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && (anchor.current?.contains(target) === true || menu.current?.contains(target) === true);
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close(true);
    };
    // 목록 안을 굴리는 것은 닫을 일이 아니다 — 값이 많으면 목록이 제 안에서 스크롤된다.
    const onScroll = (event: Event): void => {
      if (!inside(event.target)) close(false);
    };
    const onResize = (): void => close(false);
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  const toggle = (value: string): void => {
    onChange(chosen.includes(value) ? chosen.filter((v) => v !== value) : [...chosen, value]);
  };

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`th-filter${active ? ' is-active' : ''}`}
        aria-label={active ? `${label} 필터 — ${chosen.length}개 선택` : `${label} 필터`}
        title={active ? `${label} 필터 — ${chosen.length}개 선택` : `${label} 필터`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <ListFilter aria-hidden="true" strokeWidth={2.25} />
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              id={id}
              role="dialog"
              aria-label={`${label} 필터`}
              className="col-filter"
              style={position ? { top: position.top, left: position.left } : { top: -9999, left: -9999 }}
            >
              <label className="col-filter__row col-filter__all">
                <input type="checkbox" checked={!active} onChange={() => onChange([])} />
                <span className="col-filter__label">전체</span>
                <span className="col-filter__count">{total}</span>
              </label>
              <div className="col-filter__list">
                {options.map((option) => (
                  <label key={option.value} className="col-filter__row">
                    <input
                      type="checkbox"
                      checked={chosen.includes(option.value)}
                      onChange={() => toggle(option.value)}
                    />
                    <span className="col-filter__label">
                      {option.label}
                      {option.hint ? <span className="col-filter__hint">{option.hint}</span> : null}
                    </span>
                    <span className="col-filter__count">{option.count}</span>
                  </label>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * 걸러 보고 있다는 표시 — 표 바로 위 한 줄.
 *
 * 깔때기 색만으로는 놓친다. 몇 줄이 빠졌는지와 푸는 단추를 함께 둬야 "목록에 없다" 를
 * "걸러져 있다" 로 읽는다.
 */
export function FilterNote({
  shown,
  total,
  unit,
  onClear,
}: {
  shown: number;
  total: number;
  unit: string;
  onClear: () => void;
}): ReactNode {
  return (
    <div className="filter-note">
      <span>
        필터 결과 <strong>{shown}{unit}</strong> · 전체 {total}{unit}
      </span>
      <button type="button" className="link" onClick={onClear}>
        필터 해제
      </button>
    </div>
  );
}
